import { useCallback, useEffect, useRef, useState } from "react";
import { toast } from "sonner";

import { anunciosMetaApi, mensagemDeErro } from "../api";
import { atualizarAvisosMeta } from "../avisos/avisosMeta";

const RELER_EM_ANALISE_MS = 60000;   // com anúncio em análise, o cartão se atualiza sozinho

/** Os cartões das campanhas criadas pelo Núcleo e as ações de cada um. */
export default function useCampanhasNucleo(recarregarEm) {
  const [cartoes, setCartoes] = useState(null);
  const [horario, setHorario] = useState(null);       // horário de funcionamento da loja, para o diálogo "Horário"
  const [ocupada, setOcupada] = useState(null);       // id da campanha com ação em andamento
  const vivo = useRef(true);
  useEffect(() => () => { vivo.current = false; }, []);

  const carregar = useCallback(async () => {
    try {
      const r = await anunciosMetaApi.cartoes();
      if (vivo.current) { setCartoes(r.campanhas || []); setHorario(r.horario || null); }
    } catch { if (vivo.current) setCartoes((atual) => atual || []); }
  }, []);
  useEffect(() => { carregar(); }, [carregar, recarregarEm]);

  const emAnalise = (cartoes || []).some((c) => c.situacao.codigo === "em_analise");
  useEffect(() => {
    if (!emAnalise) return undefined;
    const timer = setInterval(() => { carregar(); atualizarAvisosMeta(); }, RELER_EM_ANALISE_MS);
    return () => clearInterval(timer);
  }, [emAnalise, carregar]);

  /** Roda a ação, troca o cartão pelo que o servidor devolveu e avisa. Devolve true se deu certo. */
  const executar = useCallback(async (id, chamada, sucesso) => {
    setOcupada(id);
    try {
      const r = await chamada();
      if (r?.campanha) setCartoes((atual) => (atual || []).map((c) => (c.meta_campaign_id === id ? r.campanha : c)));
      if (sucesso) toast.success(sucesso);
      return true;
    } catch (e) {
      toast.error(mensagemDeErro(e, "Não foi possível fazer isso agora."));
      return false;
    } finally { if (vivo.current) setOcupada(null); }
  }, []);

  return {
    cartoes, horario, ocupada, carregar,
    pausar: (id) => executar(id, () => anunciosMetaApi.acaoDaCampanha(id, "pausar"), "Campanha pausada. Nada é gasto enquanto ela está parada."),
    retomar: (id) => executar(id, () => anunciosMetaApi.acaoDaCampanha(id, "retomar"), "Campanha de volta ao ar."),
    encerrar: (id) => executar(id, () => anunciosMetaApi.acaoDaCampanha(id, "encerrar"), "Campanha encerrada."),
    mudarOrcamento: (id, centavos) => executar(id, () => anunciosMetaApi.mudarOrcamento(id, centavos), "Valor por semana atualizado."),
    mudarPeriodo: (id, dados) => executar(id, () => anunciosMetaApi.mudarPeriodo(id, dados), "Período atualizado."),
    mudarHorario: (id, modo) => executar(id, () => anunciosMetaApi.mudarHorario(id, modo),
      modo === "funcionamento" ? "Agora o anúncio só aparece com a loja aberta." : "Agora o anúncio aparece o dia todo."),
    pausarAnuncio: (id, anuncioId) => executar(id, () => anunciosMetaApi.acaoDoAnuncio(id, anuncioId, "pausar"), "Anúncio pausado."),
    retomarAnuncio: (id, anuncioId) => executar(id, () => anunciosMetaApi.acaoDoAnuncio(id, anuncioId, "retomar"), "Anúncio retomado."),
  };
}
